/**
 * Trainingsarten eines Studios — welche Angebote es gibt und ob daraus ein
 * Einlass folgt.
 *
 *   Kurstraining   Training findet in gebuchten Kursen statt. Anwesenheit
 *                  ergibt sich aus der Buchung (calendar_bookings), es gibt
 *                  keinen Einlass und keine Anwesenheitsliste.
 *   Freies Training  Mitglieder kommen, wann sie wollen. Nur dieser Fall
 *                  braucht Checkin/Checkout — wer da ist, weiß sonst niemand.
 *
 * Gespeichert in companies_infos, type 'training_modes', Werte als JSON in
 * `options` — dasselbe Muster wie app_config.
 *
 * ACHTUNG: Die Regeln gelten serverseitig (studio/server/services/
 * attendanceService.js, DEFAULT_MODES). Die Werte hier sind die Spiegelung für
 * die Oberfläche; wer Defaults ändert, ändert sie an beiden Stellen.
 */

export const TRAINING_MODES_TYPE = 'training_modes'

export type TrainingModes = {
  /** Kurstraining im Angebot (gebuchte Kurse, kein Einlass) */
  courseTraining: boolean
  /** Freies Training im Angebot — schaltet den Einlass frei */
  freeTraining: boolean
  /** Kappungsgrenze in Stunden, ab der der Auto-Checkout greift */
  autoCheckoutHours: number
  /** Mindestaufenthalt in Minuten, bevor ein zweiter Scan auscheckt */
  minDwellMinutes: number
  /** Sperrfenster in Sekunden nach einem Checkout */
  scanDebounceSeconds: number

  /**
   * Zutrittsprüfung am Einlassgerät. Aus: der Scan protokolliert nur, wie
   * bisher. An: der Server entscheidet vor dem Checkin, ob der Kunde eintreten
   * darf, und das Gerät weist ihn gegebenenfalls ab.
   */
  entryCheck: boolean
  /** Laufender Vertrag nötig (bei Karten zusätzlich Resteinheiten) */
  entryRequireContract: boolean
  /** Kulanz in Tagen nach Vertragsende */
  entryGraceDays: number
  /** Nur innerhalb der Öffnungszeiten des Gerätestandorts */
  entryOpeningHours: boolean
}

/**
 * Bestandsfall: ein Studio ohne gespeicherte Einstellung bietet beides an.
 * Bisher gab es die Unterscheidung nicht — wer nichts einstellt, verliert
 * nichts.
 */
export const defaultTrainingModes = (): TrainingModes => ({
  courseTraining: true,
  freeTraining: true,
  autoCheckoutHours: 4,
  minDwellMinutes: 5,
  scanDebounceSeconds: 60,
  // Aus als Default: ein eingeschalteter Entscheid stellt in einem Studio mit
  // halb gepflegten Verträgen am nächsten Morgen die Mitglieder vor die Tür.
  // Die Prüfung schaltet ein, wer weiß, dass seine Daten sie tragen.
  entryCheck: false,
  entryRequireContract: true,
  entryGraceDays: 0,
  entryOpeningHours: true,
})

const toBool = (val: unknown, fallback: boolean): boolean =>
  typeof val === 'boolean' ? val : fallback

const toNum = (val: unknown, fallback: number): number => {
  const n = Number(val)
  return Number.isFinite(n) && n >= 0 ? n : fallback
}

/**
 * `options` einer companies_infos-Zeile in geprüfte Werte übersetzen.
 * Nimmt String wie Objekt (der Sync liefert je nach Pfad beides) und fällt bei
 * kaputtem JSON auf die Defaults zurück, statt zu werfen: eine unlesbare
 * Einstellung darf keine Ansicht sprengen.
 */
export function parseTrainingModes (options: unknown): TrainingModes {
  const d = defaultTrainingModes()
  let raw: any = options
  if (typeof options === 'string') {
    try { raw = JSON.parse(options || '{}') } catch { return d }
  }
  if (!raw || typeof raw !== 'object') return d
  return {
    courseTraining: toBool(raw.courseTraining, d.courseTraining),
    freeTraining: toBool(raw.freeTraining, d.freeTraining),
    autoCheckoutHours: toNum(raw.autoCheckoutHours, d.autoCheckoutHours),
    minDwellMinutes: toNum(raw.minDwellMinutes, d.minDwellMinutes),
    scanDebounceSeconds: toNum(raw.scanDebounceSeconds, d.scanDebounceSeconds),
    entryCheck: toBool(raw.entryCheck, d.entryCheck),
    entryRequireContract: toBool(raw.entryRequireContract, d.entryRequireContract),
    entryGraceDays: toNum(raw.entryGraceDays, d.entryGraceDays),
    entryOpeningHours: toBool(raw.entryOpeningHours, d.entryOpeningHours),
  }
}

/**
 * Mindestens eine Trainingsart muss aktiv bleiben. Ein Studio, das weder
 * Kurse noch freies Training anbietet, hat kein Angebot — und die Oberfläche
 * hätte keinen sinnvollen Zustand mehr anzuzeigen.
 */
export function isValidTrainingModes (modes: TrainingModes): boolean {
  return modes.courseTraining || modes.freeTraining
}

/** Folgt aus den Trainingsarten ein Einlass? Die einzige Stelle, die das entscheidet. */
export function hasAccessControl (modes: TrainingModes): boolean {
  return modes.freeTraining
}

/**
 * Entscheidet der Einlass über den Zutritt — oder protokolliert er nur?
 *
 * Zwei Dinge, die im Sprachgebrauch leicht zusammenfallen: `hasAccessControl`
 * heißt „es gibt überhaupt einen Einlass" (freies Training), `checksEntry`
 * heißt „dieser Einlass weist auch ab". Ohne freies Training gibt es kein
 * Einlassgerät, an dem die Prüfung greifen könnte.
 */
export function checksEntry (modes: TrainingModes): boolean {
  return modes.freeTraining && modes.entryCheck
}
